import React from 'react';
import PropTypes from 'prop-types';
import { List, Radio, SingleRange, DualRange } from '@prisjakt/ui-filters';
import { bemGenerator } from '@prisjakt/ui-base';
import { AppContext } from '@prisjakt/ui-app-context';
import Button from '@prisjakt/ui-button';

import './index.scss';

const styles = bemGenerator('filter-filter', ['input']);

class Filter extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      expanded: [],
      inputValue: props.query ? props.query[props.displayFilter.id] : '',
    };

    this.handleInputKeyPress = this.handleInputKeyPress.bind(this);
    this.handleRadioOnChange = this.handleRadioOnChange.bind(this);
    this.handleCheckboxOnChange = this.handleCheckboxOnChange.bind(this);
    this.handleOnExpand = this.handleOnExpand.bind(this);
  }

  handleInputKeyPress(displayFilter, event) {
    if (event.key === 'Enter') {
      const { categoryId } = this.props;

      this.props.filterChangeAction(categoryId, {
        id: displayFilter.id,
        value: event.target.value,
        children: undefined,
        section: {
          name: displayFilter.name,
          unit: displayFilter.unit,
        },
        parents: [],
        filterType: 'string',
      });
      event.target.blur();
    }
  }

  handleRadioOnChange(displayFilter, selected) {
    const { categoryId } = this.props;

    this.props.filterChangeAction(categoryId, {
      id: displayFilter.id,
      section: { name: displayFilter.name, unit: displayFilter.unit },
      value: selected.id,
      filterType: 'boolean',
    });
  }

  handleCheckboxOnChange(displayFilter, filterType, selected) {
    const { categoryId } = this.props;

    this.props.filterChangeAction(categoryId, {
      ...selected,
      id: displayFilter.id,
      section: {
        name: displayFilter.name,
        unit: displayFilter.unit,
      },
      filterType,
    });
  }

  handleOnExpand(id, event) {
    event.preventDefault();
    this.setState(prevState => ({
      expanded: prevState.expanded.includes(id)
        ? prevState.expanded.filter(item => item !== id)
        : prevState.expanded.concat([id]),
    }));
  }

  render() {
    const {
      displayFilter,
      filteredValues,
      query,
      layout,
      showAllValues,
      onToggleAllValues,
    } = this.props;
    const { t } = this.context;

    if (!displayFilter.type) return null;

    switch (displayFilter.type) {
      case 'boolean-reduced':
      case 'boolean': {
        const filterVal =
          filteredValues[displayFilter.id] &&
          filteredValues[displayFilter.id].values
            ? filteredValues[displayFilter.id].values
            : {};
        return (
          <Radio
            id={displayFilter.id}
            key={displayFilter.id}
            selected={query[displayFilter.id]}
            layout={layout}
            values={[
              {
                id: true,
                text: t('filter', 'properties.select-true'),
                products: filterVal.true,
              },
              {
                id: false,
                text: t('filter', 'properties.select-false'),
                products: filterVal.false,
              },
            ]}
            onChange={selected =>
              this.handleRadioOnChange(displayFilter, selected)
            }
          />
        );
      }
      case 'list-or':
      case 'list-and':
      case 'list-or-reduced':
      case 'list-and-reduced':
      case 'list-tree-and':
      case 'list-tree-or': {
        if (!filteredValues[displayFilter.id].values) {
          return null;
        }

        const showExpansion = filteredValues[displayFilter.id]
          ? filteredValues[displayFilter.id].values.length > 5
          : false;

        const isLimited = showExpansion && showAllValues === false;
        const limitedValues = isLimited
          ? filteredValues[displayFilter.id].values.slice(
              0,
              this.props.limitValues
            )
          : filteredValues[displayFilter.id].values;

        return (
          <div
            key={`${displayFilter.id}_wrapper`}
            className={styles.block.withModifier({
              [layout]: true,
            })}
          >
            <List
              id={displayFilter.id}
              key={displayFilter.id}
              values={limitedValues}
              selected={query[displayFilter.id]}
              autoSelectChildren={false}
              onExpand={(event, id) => this.handleOnExpand(id, event)}
              expanded={this.state.expanded}
              layout={layout}
              onChange={selected =>
                this.handleCheckboxOnChange(displayFilter, 'list', selected)
              }
            />

            {showExpansion && layout === 'vertical' && (
              <Button
                variant="text"
                color="link"
                onClick={() => onToggleAllValues(displayFilter.id)}
                data-test="MoreFiltersToggleButton"
              >
                {t(
                  'filter',
                  isLimited
                    ? 'properties.quantity-show-more'
                    : 'properties.quantity-show-less'
                )}
              </Button>
            )}
          </div>
        );
      }
      case 'date-year':
      case 'integer':
      case 'decimal': {
        return (
          <SingleRange
            id={displayFilter.id}
            key={displayFilter.id}
            type={displayFilter.type}
            values={filteredValues[displayFilter.id]}
            selected={query[displayFilter.id]}
            layout={layout}
            onChange={selected =>
              this.handleCheckboxOnChange(displayFilter, 'slider', selected)
            }
          />
        );
      }
      case 'interval-decimal-touch':
      case 'interval-decimal-contain':
      case 'interval-decimal-span':
      case 'interval-integer-touch':
      case 'interval-integer-contain':
      case 'interval-integer-span': {
        return (
          <DualRange
            id={displayFilter.id}
            key={displayFilter.id}
            type={displayFilter.type}
            values={filteredValues[displayFilter.id]}
            selected={query[displayFilter.id]}
            layout={layout}
            onChange={selected =>
              this.handleCheckboxOnChange(displayFilter, 'slider', selected)
            }
          />
        );
      }
      case 'string': {
        return (
          <input
            key={displayFilter.id}
            className={styles.input}
            placeholder={displayFilter.name}
            value={this.state.inputValue}
            layout={layout}
            onKeyPress={event => this.handleInputKeyPress(displayFilter, event)}
            onChange={event => {
              this.setState({ inputValue: event.target.value });
            }}
          />
        );
      }
      default: {
        return (
          <div>
            {displayFilter.name} with
            {displayFilter.type}
          </div>
        );
      }
    }
  }
}

Filter.propTypes = {
  categoryId: PropTypes.string.isRequired,
  layout: PropTypes.oneOf(['vertical', 'horizontal']).isRequired,
  filterChangeAction: PropTypes.func,
  displayFilter: PropTypes.shape({
    id: PropTypes.string,
    item_type: PropTypes.string,
    name: PropTypes.string,
    children: PropTypes.array,
  }).isRequired,
  query: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  filteredValues: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onToggleAllValues: PropTypes.func.isRequired,
  limitValues: PropTypes.number,
  showAllValues: PropTypes.bool.isRequired,
};

Filter.defaultProps = {
  filterChangeAction: () => {},
  query: null,
  filteredValues: null,
  limitValues: Infinity,
};

Filter.contextType = AppContext;

export default Filter;
